import React, {
  useState, useMemo,
} from 'react';
import toast from 'react-hot-toast';
import {
  Modal, Button, Message, Input, Icon,
} from 'semantic-ui-react';
import { Sticky, PaginationWithLimiter } from 'components/lib/UI';
import api from 'api';
import util from 'utils/utils';
import {
  DeleteNameContainer, DeleteNameItem,
} from './Styles';

const updateIdea = (setIdeaState, ideaId, data) => {
  setIdeaState(prevState => ({
    ...prevState,
    ideas: Object.assign([], prevState.ideas.map((i) => {
      if (i._id !== ideaId) return i;
      return { ...i, ...data };
    })),
  }));
};

const pinIdea = (setIdeaState, ideas, ideaId, isPinned) => {
  const originalIdeas = Object.assign([], ideas);
  updateIdea(setIdeaState, ideaId, { isPinned });
  api.ideas.updateStatus(ideaId, { isPinned }, () => {
    toast.success(isPinned ? 'Idea pinned' : 'Idea unpinned');
  }, () => {
    toast.error('Failed to pin idea');
    setIdeaState(prevState => ({ ...prevState, ideas: originalIdeas }));
  });
};

const legacyStampMapper = {
  green_apple: 'green',
  full_moon: 'yellow',
  large_orange_diamond: 'orange',
  red_circle: 'red',
  aquarius: 'purple',
  large_blue_circle: 'blue',
  ribbon: 'pink',
  green: 'green_apple',
  yellow: 'full_moon',
  orange: 'large_orange_diamond',
  red: 'red_circle',
  purple: 'aquarius',
  blue: 'large_blue_circle',
  pink: 'ribbon',
};

const labelIdea = (ideas, ideaId, label, isApplied, toggleStamp, setIdeaState) => {
  const originalIdeas = Object.assign([], ideas);
  const idea = originalIdeas.find(i => i._id === ideaId);
  if (!idea) {
    return;
  };
  const { stamps = [] } = idea;
  let existingIndex = stamps.indexOf(label);
  if (label in legacyStampMapper) {
    existingIndex = stamps.indexOf(legacyStampMapper[label]);
    if (existingIndex < 0) existingIndex = stamps.indexOf(label);
  }
  if (isApplied && existingIndex === -1) stamps.push(label);
  if (!isApplied && existingIndex > -1) stamps.splice(existingIndex, 1);
  toggleStamp(label);
  setIdeaState(prevState => ({
    ...prevState,
    ideas: Object.assign([], prevState.ideas.map((i) => {
      if (i._id !== ideaId) return i;
      return { ...i, stamps };
    })),
  }));
  api.ideas.updateStatus(ideaId, { stamps }, () => {
    toast.success('Label applied');
  }, () => {
    setIdeaState(prevState => ({ ...prevState, ideas: originalIdeas }));
    toast.error('Failed to apply label');
  });
};

const updateAssessors = (apiFunc, challengeId, ideaIds, userIds, ideas, setIdeaState) => {
  apiFunc(challengeId, ideaIds, userIds, ({ ideas: assessorIdeas }) => {
    const updatedIdeas = Object.assign([], ideas).map((idea) => {
      const updatedIdea = assessorIdeas.find(i => i._id === idea._id);
      if (!updatedIdea) return idea;
      return { ...idea, assessments: updatedIdea.assessments };
    });
    setIdeaState(prevState => ({ ...prevState, ideas: updatedIdeas }));
    toast.success('Assessors updated');
  }, err => toast.error(err.message));
};

const bulkUpdateAssessors = (users, challengeId, assessmentAssignIdeas, ideas, setIdeaState) => {
  const existingUsers = users.filter(u => !u.isEmailInvitee);
  if (!existingUsers.length) return;
  updateAssessors(api.challenges.assignAssessors, challengeId, assessmentAssignIdeas, existingUsers.map(u => u._id), ideas, setIdeaState);
};

const bulkRemoveAssessors = (ideaIds, challengeId, userIds, setAssessmentAssignIdeas, ideas, setIdeaState) => {
  setAssessmentAssignIdeas([]);
  updateAssessors(api.challenges.unassignAssessors, challengeId, ideaIds, userIds, ideas, setIdeaState);
};

const bulkUpdateStatus = (challengeId, selectedIdeas, setIdeaState, statusKey, status) => {
  api.challenges.updateIdeasStatus(challengeId, { ideas: selectedIdeas, [statusKey]: status }, ({ ideas }) => {
    toast(`${selectedIdeas.length} ideas updated`);
    setIdeaState(prevState => ({
      ...prevState,
      ideas: prevState.ideas.map((i) => {
        const updatedIdea = ideas.find(u => u._id === i._id);
        if (!updatedIdea) return i;
        return { ...i, ...updatedIdea };
      }),
    }));
  });
};

const bulkMoveIdeas = (setIdeaState, setSelectedIdeas, ideaIds, challengeId, onComplete = () => { }) => {
  api.ideas.moveToChallenge(ideaIds, challengeId, () => {
    toast.success('Ideas moved to challenge');
    setSelectedIdeas([]);
    setIdeaState(prevState => ({ ...prevState, ideas: prevState.ideas.filter(i => !ideaIds.includes(i._id)) }));
    if (onComplete) onComplete();
  }, err => toast.error(err.message));
};

const voteForIdea = (setIdeaState, ideaId, ideas, ideasVotedFor, addVote, onFollow) => {
  const originalIdeas = Object.assign([], ideas);
  const oldIdeasVotedFor = ideasVotedFor;
  setIdeaState(prevState => ({
    ...prevState,
    ideas: originalIdeas.map((i) => {
      if (i._id !== ideaId) return i;
      const newIdea = { ...i };
      if (!newIdea.voteCount) newIdea.voteCount = 0;
      newIdea.votedFor = !!addVote;
      newIdea.voteCount += (addVote ? 1 : -1);
      return newIdea;
    }),
  }));
  api.ideas.vote(ideaId, addVote, ({ addedVotesFor, removedVotesFor }) => {
    originalIdeas.forEach((idea) => {
      if (addedVotesFor.indexOf(idea._id) > -1) {
        api.journey.record('ideas', ideaId, 'addedIdeaVote');
        onFollow('challenge', idea.challenge);
      }
      if (removedVotesFor.indexOf(idea._id) > -1) {
        api.journey.record('ideas', ideaId, 'removedIdeaVote');
      }
      const countIdeasVoted = (addedVotesFor.length - removedVotesFor.length);
      setIdeaState(prevState => ({ ...prevState, ideasVotedFor: prevState.ideasVotedFor + countIdeasVoted }));
    });
  }, (err) => {
    setIdeaState(prevState => ({ ...prevState, ideas: originalIdeas, ideasVotedFor: oldIdeasVotedFor }));
    toast.error(err.message);
  });
};

const addTag = (org, setIdeaState, updateTagList, ideas, idea, tag) => {
  api.tags.tagSingle(org, 'ideas', tag._id, idea._id, (updatedTag) => {
    setIdeaState(prevState => ({
      ...prevState,
      ideas: prevState.ideas.map((i) => {
        if (i._id !== idea._id) return i;
        const updatedIdea = { ...i };
        if (!updatedIdea.tags) {
          updatedIdea.tags = [];
          updatedIdea.fullTags = [];
        }
        updatedIdea.tags = [...updatedIdea.tags, updatedTag._id];
        updatedIdea.fullTags = [...updatedIdea.fullTags, updatedTag];
        return updatedIdea;
      }),
    }), err => toast.error(err.message));

    updateTagList(updatedTag);
  });
};

const unTag = (org, setIdeaState, updateTagList, idea, tag) => {
  api.tags.untagSingle(org, 'ideas', tag._id, idea._id, (updatedTag) => {
    const fullTags = [...idea.fullTags];
    const cIndex = fullTags.findIndex(t => t._id === updatedTag._id);
    fullTags.splice(cIndex, 1);

    setIdeaState(prevState => ({
      ...prevState,
      ideas: prevState.ideas.map((i) => {
        if (i._id !== idea._id) return i;
        const updatedIdea = { ...i };
        updatedIdea.tags = Object.assign([], updatedIdea.tags.filter(t => t !== tag._id));
        updatedIdea.fullTags = Object.assign([], updatedIdea.fullTags.filter(t => t._id !== tag._id));
        return updatedIdea;
      }),
    }));

    updateTagList(updatedTag);
  });
};

const bulkDelete = (setIdeaState, ideas, success) => {
  util.confirm(
    'Delete ideas',
    'Are you absolutely sure you want to permanently delete these ideas?',
  )
    .then(() => {
      api.ideas.bulkDelete(ideas, ({
        childrenToReturn,
      }) => {
        setIdeaState(prevState => ({
          ...prevState,
          ideas: [...prevState.ideas.filter(i => ideas.indexOf(i._id) === -1), ...childrenToReturn],
        }));
        success();
      }, err => toast.error(err.message));
    }).catch(() => { });
};

const Pagination = ({
  limit, page, total, updateFilters, limitValues,
}) => (
  <Sticky
    bottom={15}
    style={{
      textAlign: 'center', zIndex: 10, display: 'flex', justifyContent: 'center',
    }}
  >
    <PaginationWithLimiter
      limit={limit}
      page={page}
      total={total}
      limitValues={limitValues}
      shadowed
      updateLimit={value => updateFilters({ ideaLimit: value })}
      updatePage={value => updateFilters({ ideaPage: value })}
    />
  </Sticky>
);

const IdeaDeletionModal = ({
  ideasToDelete = [], setIdeasToDelete, setIdeaState, setSelectedIdeas,
}) => {
  const [deleteName, setDeleteName] = useState('');

  // Get all child ideas from all ideas to delete by flattening
  const allChildIdeas = useMemo(() => {
    const allChildren = [];
    ideasToDelete.forEach((idea) => {
      if (idea.ownerChildren) {
        allChildren.push(...idea.ownerChildren);
      }
    });
    return allChildren;
  }, [ideasToDelete]);

  return (
    <Modal
      open={!!ideasToDelete.length}
      onClose={() => setIdeasToDelete([])}
    >
      <Modal.Header>
        Delete ideas
      </Modal.Header>
      <Modal.Content>
        <p>
          You are choosing to delete the following ideas:
        </p>
        <ul>
          {ideasToDelete.map(idea => (
            <li>
              {idea.children
                ? (
                  <Icon name='sitemap' />
                ) : null
              }
              <b>
                {idea.name}
                {idea.owner ? ` - ${idea.owner.profile.fullName}` : ''}
              </b>
            </li>
          ))}
        </ul>
        {allChildIdeas.length
          ? (
            <>
              <p>The following child ideas will be removed from the deleted merged ideas, and returned to the challenge as individual ideas.</p>
              <ul>
                {allChildIdeas.map((idea) => (
                  <li>
                    <b>
                      {idea.name}
                    </b>
                  </li>
                ))}
              </ul>
              <p>Any comments left on the merged ideas will be deleted, but any comments left on the children will be returned to them.</p>
            </>
          ) : null
        }
        <Message error>
          This action is irreversible. These ideas will be permanently deleted, and cannot be recovered. Please make sure this action is correct, and double check the ideas you have chosen.
        </Message>
        <DeleteNameContainer>
          <DeleteNameItem>
            <p>
              Type the word 'DELETE' below to confirm you understand this.
            </p>
            <Input
              placeholder="DELETE"
              value={deleteName}
              onChange={e => setDeleteName(e.target.value)}
            />
          </DeleteNameItem>
        </DeleteNameContainer>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => setIdeasToDelete([])}>Cancel</Button>
        <Button
          disabled={deleteName !== 'DELETE'}
          color="red"
          onClick={() => bulkDelete(
            setIdeaState,
            ideasToDelete.map(i => i._id),
            () => {
              setSelectedIdeas(prev => prev.filter(p => ideasToDelete.map(i => i._id).indexOf(p) === -1));
              setIdeasToDelete([]);
            },
          )
          }
        >
          Delete
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export {
  updateIdea,
  pinIdea,
  labelIdea,
  updateAssessors,
  bulkUpdateAssessors,
  bulkRemoveAssessors,
  bulkUpdateStatus,
  bulkMoveIdeas,
  voteForIdea,
  addTag,
  unTag,
  Pagination,
  IdeaDeletionModal,
};
